import { LockOpenRounded, LockRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import BlotterNote from "@/components/BlotterNote";
import { DATE_INPUT_LANG } from "@/constants/dates";
import { usePeriods } from "@/hooks/useAccounting";
import { type AccountingPeriod, PERIOD_STATUS } from "@/interfaces/accounting";
import { formatDate } from "@/utils/date-format";

const money = (v: string | null) =>
  Number(v ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "2026-08-01" → "August 2026". A period is named by its month, never by its first day. */
export const periodLabel = (period: string) =>
  new Date(`${period.slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

const th = {
  fontSize: 11,
  letterSpacing: "0.6px",
  textTransform: "uppercase" as const,
  color: "var(--color-graphite)",
  fontWeight: 600,
  borderBottom: "1px solid var(--color-card-border)",
  whiteSpace: "nowrap" as const,
};

/**
 * The Periods blotter. One row per month, with the totals computed from the ledger rather than
 * stored — a stored total that disagrees with the entries is worse than no total at all.
 *
 * The period reference opens its posting batches, which is where the close cycle is actually run.
 */
export default function PeriodsListPanel({
  onOpenPeriod,
}: {
  onOpenPeriod: (period: string) => void;
}) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data: periods = [], isLoading } = usePeriods({
    from: from || undefined,
    to: to || undefined,
  });

  return (
    <Box sx={{ fontFamily: "var(--font-lato)" }}>
      <BlotterNote>
        One row per accounting month. Click a period reference to see its posting batches, export to
        QuickBooks, and record the close.
      </BlotterNote>

      <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          label="From"
          type="date"
          lang={DATE_INPUT_LANG}
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="To"
          type="date"
          lang={DATE_INPUT_LANG}
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {(from || to) && (
          <Button
            size="small"
            onClick={() => {
              setFrom("");
              setTo("");
            }}
            sx={{ textTransform: "none" }}
          >
            Clear
          </Button>
        )}
        {/* A date inside a month selects that whole month — a period is the unit, not the day. */}
        <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
          Any date selects the month it falls in.
        </Typography>
      </Box>

      <Box
        sx={{
          border: "1px solid var(--color-card-border)",
          borderRadius: "var(--radius-card)",
          overflowX: "auto",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={th}>Period Ref</TableCell>
              <TableCell sx={th}>From</TableCell>
              <TableCell sx={th}>To</TableCell>
              <TableCell sx={th}>Status</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Dr</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Cr</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Entries</TableCell>
              <TableCell sx={th}>Signed Off Date</TableCell>
              <TableCell sx={th}>Signed By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={9} sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && periods.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                  No periods in that range.
                </TableCell>
              </TableRow>
            )}
            {periods.map((p: AccountingPeriod) => {
              const closed = p.status === PERIOD_STATUS.closed;
              return (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onOpenPeriod(p.period)}
                      sx={{
                        appearance: "none",
                        background: "none",
                        border: "none",
                        p: 0,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "var(--color-rodeo-black)",
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                      }}
                    >
                      {periodLabel(p.period)}
                    </Box>
                    {/* Held entries never reach a batch, so the count belongs where the month is
                        judged rather than only on the export screen. */}
                    {p.heldCount > 0 && (
                      <Chip
                        size="small"
                        label={`${p.heldCount} held`}
                        color="warning"
                        variant="outlined"
                        sx={{ ml: 1, height: 20, fontSize: 11 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{formatDate(p.periodFrom)}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{formatDate(p.periodTo)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={
                        closed ? (
                          <LockRounded fontSize="small" />
                        ) : (
                          <LockOpenRounded fontSize="small" />
                        )
                      }
                      label={closed ? "Closed" : "Open"}
                      color={closed ? "default" : "success"}
                      variant={closed ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "right", fontSize: 13, fontVariantNumeric: "tabular-nums" }}
                  >
                    {money(p.totalDebit)}
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "right", fontSize: 13, fontVariantNumeric: "tabular-nums" }}
                  >
                    {money(p.totalCredit)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "right", fontSize: 13 }}>{p.entryCount}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {p.closedAt ? formatDate(p.closedAt) : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {p.closedByName ?? "—"}
                    {p.reopenedAt && (
                      <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                        reopened {formatDate(p.reopenedAt)}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
